import { useMemo } from 'react';

import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import {
  FillInBlanksAnswer,
  FillInBlanksSentence as FillInBlanksSentenceType,
  ViewProps,
} from './fill-in-blanks.constants';
import * as Styled from './fill-in-blanks-edit.style';
import { mergeFillInBlanksBlocks } from './logic/merge-fill-in-blanks-blocks';
import { getFillInBlanksAnswerColour } from './logic/get-fill-in-blanks-answer-colour';
import { sortFillInBlanksSentences } from './logic/sort-fill-in-blanks-sentences';
import FillInBlanksTextBlock from './fill-in-blanks-text-block';
import { FillInBlanksSentencesWrapper } from './fill-in-blanks-shared.style';
import { FontFamily, FontSize } from './use-fill-in-blanks-font-sizes';

const AnswerBlock = ({
  componentId,
  answer,
  fontSize,
  fontFamily,
  onSelectSpace,
}: {
  componentId: string;
  answer: FillInBlanksAnswer;
  fontSize: FontSize | null;
  fontFamily: FontFamily;
  onSelectSpace: (itemId: string) => void;
}) => {
  const { editModeTargetElement, activeComponent } =
    useActivityBuilderContext();

  const colours = useMemo(() => getFillInBlanksAnswerColour(answer), [answer]);

  const shouldShowLabel = useMemo(
    () => !!(answer.showLabel && answer.label),
    [answer]
  );

  return (
    <Styled.FillInBlanksField
      $colours={colours}
      $minWidth={answer.width || 100}
      $punctuation={answer.punctuation}
      $hasLabel={shouldShowLabel}
      $fontSize={fontSize}
      $fontFamily={fontFamily}
    >
      <Styled.FillInBlanksAnswer
        $colours={colours}
        $minWidth={answer.width || 100}
        $label={shouldShowLabel ? answer.label : null}
        $fontSize={fontSize}
        $fontFamily={fontFamily}
      >
        <Styled.FillInBlanksEditTipTapWrapper
          $placeholder={answer.placeholder}
          $isScaffold={answer.showPlaceholderAsScaffold}
          $fontSize={fontSize}
          $fontFamily={fontFamily}
        >
          <Styled.FillInBlanksEditTipTap
            className="tiptap"
            $fontSize={fontSize}
            $fontFamily={fontFamily}
          >
            <p>
              <br className="ProseMirror-trailingBreak" />
            </p>
          </Styled.FillInBlanksEditTipTap>
        </Styled.FillInBlanksEditTipTapWrapper>
      </Styled.FillInBlanksAnswer>
      <Styled.FillInBlanksEditTriggerAnswer
        type="button"
        $isActive={
          activeComponent?.id === componentId &&
          editModeTargetElement === answer.itemId
        }
        onClick={() => onSelectSpace(answer.itemId)}
      />
    </Styled.FillInBlanksField>
  );
};

const Sentence = ({
  componentId,
  sentence,
  fontSize,
  fontFamily,
  onSelectSpace,
}: {
  componentId: string;
  sentence: FillInBlanksSentenceType;
  fontSize: FontSize | null;
  fontFamily: FontFamily;
  onSelectSpace: (itemId: string) => void;
}) => {
  const mergedBlocks = useMemo(
    () => mergeFillInBlanksBlocks(sentence),
    [sentence]
  );

  return mergedBlocks.map(block => {
    return 'text' in block ? (
      <FillInBlanksTextBlock key={block.itemId} textBlock={block} />
    ) : (
      <AnswerBlock
        key={`${block.itemId}-${block.showPlaceholderAsScaffold}-${block.placeholder}`}
        answer={block}
        componentId={componentId}
        onSelectSpace={onSelectSpace}
        fontSize={fontSize}
        fontFamily={fontFamily}
      />
    );
  });
};

const FillInBlanksEditFields = ({
  id,
  properties,
  editModeTargetElement,
  setEditModeTargetElement,
}: ViewProps & {
  editModeTargetElement?: string;
  setEditModeTargetElement: (id: string) => void;
}) => {
  const { activeComponent } = useActivityBuilderContext();

  const sortedSentences = useMemo(
    () => sortFillInBlanksSentences(properties.fillInBlanks.sentences),
    [properties.fillInBlanks.sentences]
  );

  return (
    <FillInBlanksSentencesWrapper
      $fontFamily={properties.fillInBlanks.fontFamily}
      $fontSize={properties.fillInBlanks.fontSize}
    >
      {sortedSentences.map(sentence => (
        <Styled.FillInBlanksEditSentence key={sentence.itemId}>
          <Sentence
            sentence={sentence}
            componentId={id}
            onSelectSpace={setEditModeTargetElement}
            fontSize={properties.fillInBlanks.fontSize}
            fontFamily={properties.fillInBlanks.fontFamily}
          />
          <Styled.FillInBlanksEditTrigger
            type="button"
            $showOutline
            $isActive={
              activeComponent?.id === id &&
              editModeTargetElement === sentence.itemId
            }
            onClick={() => setEditModeTargetElement(sentence.itemId)}
          />
        </Styled.FillInBlanksEditSentence>
      ))}
    </FillInBlanksSentencesWrapper>
  );
};

export default FillInBlanksEditFields;
